import { useRef, useState } from 'react';

import { Input, InputGroup, SimpleGrid, Stack, Text, useBreakpointValue } from '@chakra-ui/react';
import { isEmpty, sortBy } from 'lodash';
import { Sheet } from 'react-modal-sheet';
import { useQuery } from 'react-query';

import { ReactComponent as CloseIcon } from '../../../icons/xmark.svg';
import { queryGet } from '@pocapan/shared/utils/queryClient';

import { t } from 'i18next';
import { Virtuoso } from 'react-virtuoso';
import styled from 'styled-components';
import { EventNameBox } from '../../event/EventNameBox';
import LikedMembersList from '../../lists/LikedMembersList';
import TradingPhotoCardWindowList from '../../lists/TradingPhotoCardWindowList';
import AddTradingPhotoCard from '../../photocard/AddTradingPhotoCard';
import ArtistIconSelector from '../../selectors/ArtistIconSelector';
import EmptyPhotoCardEventSkeletonV2 from '../../skeletons/EmptyPhotoCardEventSkeletonV2';
import { filterSearchablePhotoCards } from '@pocapan/shared/utils/SearchPhotoCard';
import GradientLoadingSpinner from '../../../presentation/components/GradientLoadingSpinner';
import usePhotoCardEventsQuery from '../../../presentation/home/hooks/usePhotoCardEventsQuery';

const EventNameBoxWrapper = styled.div`
  overflow: hidden;
  padding-bottom: 8px;
`;

export default function AddPhotoCardBottomSheet({
  title,
  isCheckPhotoCardBottomSheetOpen,
  setIsCheckPhotoCardBottomSheetOpen,
  onConfirmedPhotoCards
}) {
  const containerRef = useRef();
  const headerRef = useRef();

  const [selectedArtist, setSelectedArtist] = useState();
  const [selectedMember, setSelectedMember] = useState();
  const [searchText, setSearchText] = useState();

  const artistId = selectedArtist?.artistId;

  const { data: searchablePhotoCards } = useQuery(
    ['searchablePhotoCards', artistId, selectedMember?.memberId],
    () =>
      queryGet(
        `/api/v1/photo_cards/searchable?artist_id=${artistId}&member_id=${selectedMember?.memberId}`
      ),
    {
      enabled: !!artistId && !!selectedMember?.memberId,
      select: (r) => r.photoCards
    }
  );

  const {
    data: eventsData,
    fetchNextPage: fetchNextEvents,
    hasNextPage: hasNextEvents,
    isFetchingNextPage: isFetchingNextEvents,
    isLoading: isLoadingEvents
  } = usePhotoCardEventsQuery({
    artistId: artistId || '',
    memberId: selectedMember?.memberId || '',
    perPage: 10
  });

  const eventsByMember = (eventsData?.pages?.map((p) => p.events) ?? []).flat();

  const selectPhotoCard = (photoCard, _e) => {
    onConfirmedPhotoCards(photoCard);
  };

  const onSelectedArtist = (artist) => {
    setSelectedArtist(artist);
  };

  const onSelectedMember = (member) => {
    setSelectedMember(member);
  };

  const onHandleSearchText = (text) => {
    setSearchText(text);
  };

  const scrollHeight = () => {
    if (containerRef?.current?.offsetHeight && headerRef?.current?.offsetHeight) {
      return containerRef?.current?.offsetHeight - headerRef?.current?.offsetHeight - 24;
    }
    return '60vh';
  };

  const columns = useBreakpointValue({
    base: 4, // 모바일
    md: 6, // iPad
    lg: 6 // 큰 화면
  });
  
  return (
    <Sheet
      isOpen={isCheckPhotoCardBottomSheetOpen}
      onClose={() => setIsCheckPhotoCardBottomSheetOpen(false)}
      style={{ zIndex: 2147483647 }}
    >
      <Sheet.Container
        ref={containerRef}
        className="pb-8 !bg-backgroundElevated-light dark:!bg-backgroundElevated-dark"
        style={{ borderTopLeftRadius: '20px', borderTopRightRadius: '20px' }}
      >
        <Sheet.Header ref={headerRef}>
          <SimpleGrid columns={3} alignItems="center" margin={'16px'}>
            <CloseIcon
              onClick={() => setIsCheckPhotoCardBottomSheetOpen(false)}
              className="fill-pure-black dark:fill-pure-white"
            />
            <Text fontWeight={700} fontSize={'17px'} lineHeight={'20px'} className="text-center">
              {title}
            </Text>
            <div />
          </SimpleGrid>

          <ArtistIconSelector onSelected={onSelectedArtist} enableAddCard={false} />
          <div
            style={{
              paddingTop: '4px',
              paddingLeft: '24px',
              paddingRight: '24px'
            }}
          >
            <LikedMembersList
              artistMembers={selectedArtist?.members}
              onChangeLikedMember={onSelectedMember}
              showAllMember={true}
            />
          </div>

          <InputGroup marginTop="12px" paddingX={'24px'}>
            <Input
              className="placeholder:text-onDisabled-light dark:placeholder:text-onDisabled-dark bg-surface-light dark:bg-surface-dark text-main-light-2 dark:text-main-dark-2"
              height={'45px'}
              border={'none'}
              borderRadius={'12px'}
              fontSize={'14px'}
              lineHeight={'17px'}
              fontWeight={400}
              placeholder={t('addPhotoCard.searchPlaceholder')}
              onChange={(e) => onHandleSearchText(e.target.value)}
              width={'calc(100% + 12px)'}
            />
          </InputGroup>
          <Text
            className="text-sub-light-1 dark:text-sub-dark-1"
            fontSize={'13px'}
            paddingX={'24px'}
            paddingTop={'8px'}
            paddingBottom={'24px'}
            textAlign={'left'}
          >
            {t('addPhotoCard.description')}
          </Text>
        </Sheet.Header>

        <Sheet.Content
          disableDrag={true}
          style={{
            paddingLeft: '24px',
            paddingRight: '24px',
            overflowY: 'auto'
          }}
        >
          {!isEmpty(searchText)
            ? !isEmpty(filterSearchablePhotoCards(searchablePhotoCards, searchText))
              ? <TradingPhotoCardWindowList
                  tradingPhotoCards={filterSearchablePhotoCards(searchablePhotoCards, searchText)}
                  addTradingPhotoCard={selectPhotoCard}
                  windowHeight={scrollHeight()}
                />
              : <Text className="text-sub-light-1 dark:text-sub-dark-1" textAlign="center" py={4}>
                  {t('addPhotoCard.noResults')}
                </Text>
            : isLoadingEvents
              ? <EmptyPhotoCardEventSkeletonV2 />
              : !isEmpty(eventsByMember)
                ? <Virtuoso
                    style={{ height: scrollHeight(), minHeight: 200 }}
                    defaultItemHeight={200}
                    overscan={480}
                    data={eventsByMember}
                    endReached={() => {
                      if (hasNextEvents) fetchNextEvents();
                    }}
                    components={{
                      Footer: () =>
                        isFetchingNextEvents
                          ? <div className="flex justify-center items-center py-[8px]">
                              <GradientLoadingSpinner size={24} />
                            </div>
                          : null
                    }}
                    itemContent={(_, e) => {
                      const photoCards = e?.photoCards || [];
                      return (
                        photoCards.length > 0 && (
                          <Stack key={e.eventId} paddingBottom={'18px'}>
                            <EventNameBoxWrapper>
                              <EventNameBox event={e} />
                            </EventNameBoxWrapper>
                            <SimpleGrid columns={columns} gap={'8px'} marginTop={'0px!important'}>
                              {sortBy(photoCards, (p) => {
                                return -p.collectedCount;
                              }).map((photoCard) => {
                                return (
                                  <AddTradingPhotoCard
                                    key={photoCard.photoCardId}
                                    photoCard={photoCard}
                                    onClickPhotoCard={(p, e) => selectPhotoCard(p, e)}
                                  />
                                );
                              })}
                            </SimpleGrid>
                          </Stack>
                        )
                      );
                    }}
                  />
                : <></>}
        </Sheet.Content>
      </Sheet.Container>
      <Sheet.Backdrop onClick={() => setIsCheckPhotoCardBottomSheetOpen(false)} />
    </Sheet>
  );
}
